import React, { SyntheticEvent, useEffect, useState } from 'react'
import {
    Box,
    Grid,
    Card,
    Divider,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    FormHelperText,
    Stack,
    Fab
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import moment from 'moment'
import Autocomplete from '@mui/material/Autocomplete'
import axios from 'axios'
import Swal from 'sweetalert2'

import apiUrl from '../../../../configs/constant';
import MsiDatagrid from '../../../../components/custom/MsiDatagridAuto';
import { useNavigate, useParams } from 'react-router-dom'
import Icon from '../../../../components/icon';
import { useToast } from '../../../../components/ToastProvider'
import FloatingJurnalButton from '../../../../components/acc/jurnal/FabJurnal'

/**
 * DialogAddRow
 * props:
 *  - open
 *  - onClose
 *  - onSubmit({ nomor_akun, profit_center, keterangan, total, type }) // type = 'debet' | 'kredit'
 *  - typeDefault 'debet'|'kredit'
 */
const DialogAddRow = ({ open, onClose, onSubmit, typeDefault = 'debet' }: any) => {
    const { showToast, hideToast } = useToast();
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    const [valAkun, setValAkun] = useState<any>(null)
    const [listAkun, setListAkun] = useState<any[]>([])
    const [listProfitCenter, setListProfitCenter] = useState<any[]>([])
    const [profitCenter, setProfitCenter] = useState<any>(null)

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            id: '',
            nomor_akun: '',
            nama_akun: '',
            nama_profit_center: '',
            profit_center: '',
            keterangan: '',
            total: ''
        }
    })

    const getAkun = () => {
        const url = apiUrl.apiUrl + '/acc/mutasi-kas/get-akun'
        axios
            .get(url, {
                params: {},
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    if (Array.isArray(data)) {
                        data.forEach((item: any) => {
                            if (item && item.id != null && item.nama != null) item.nama = `${item.id} | ${item.nama}`
                        })
                    }
                    setListAkun(data)
                } else {
                    Swal.fire({
                        title: 'Pemberitahuan',
                        text: response.data.message,
                        icon: 'error'
                    })
                }
            })
    }
    const getListProfitCenter = () => {
        let url = apiUrl.apiUrl + '/acc/penyesuaian/list-profit-center'

        axios
            .get(url, {
                params: {},
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                const data = response.data.data
                setListProfitCenter(data)
            })
    }

    const handleChangeAkun = (event: SyntheticEvent, newValue: any | null) => {
        setValue('nomor_akun', newValue?.id)
        setValue('nama_akun', newValue?.nama)
        setValAkun(newValue)
    }

    const handleChangeProfitCenter = (event: SyntheticEvent, newValue: any | null) => {
        setValue('profit_center', newValue?.id)
        setValue('nama_profit_center', newValue?.nama_profitcenter)
        setProfitCenter(newValue)
    }

    useEffect(() => {
        getAkun()
        getListProfitCenter()
    }, [])

    useEffect(() => {
        if (!open) {
            reset()
            setValAkun(null)
            setProfitCenter(null)
        }
    }, [open])

    const submit = (data: any) => {
        // protect: ensure numeric
        const totalNum = Number(String(data.total || '0').replace(/[^0-9.-]+/g, '')) || 0
        onSubmit({ ...data, total: totalNum, type: typeDefault })
        reset()
    }

    return (
        <Dialog open={open} fullWidth maxWidth='sm' onClose={onClose}>
            <DialogTitle>
                <Typography variant='h6' sx={{ fontWeight: 500, mb: 1.5, color: '#212121' }}>
                    Tambah {typeDefault === 'debet' ? 'Debet' : 'Kredit'}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Controller
                            name='nomor_akun'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Autocomplete
                                    value={valAkun}
                                    options={listAkun}
                                    id='nomor_akun'
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={handleChangeAkun}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option.id}>
                                                {option.nama}
                                            </li>
                                        )
                                    }}
                                    getOptionLabel={option => option.nama}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Akun'
                                            error={Boolean(errors.nomor_akun)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            )}
                        />
                        {errors.nomor_akun && (
                            <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                value={profitCenter}
                                options={listProfitCenter}
                                id='profitCenter'
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={handleChangeProfitCenter}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.id}>
                                            {option.kode} - {option.nama_profitcenter}
                                        </li>
                                    )
                                }}
                                getOptionLabel={option => option.nama_profitcenter}
                                renderInput={params => <TextField {...params} label='Profit Center' />}
                            />
                            {errors.profit_center && (
                                <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Controller
                            name='total'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <TextField {...field} label='Total' fullWidth type='number' inputProps={{ min: 0 }} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name='keterangan'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Keterangan' fullWidth multiline rows={2} />}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button color='primary' onClick={onClose}>
                    Batal
                </Button>
                <Button variant='contained' onClick={handleSubmit(submit)}>
                    Tambah
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/**
 * AddMemorialSingleTable
 * main component
 */
const AddMemorialSingleTable: React.FC = () => {
    const { showToast, hideToast } = useToast();
    const navigate = useNavigate();
    const { id } = useParams()

    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    // master form
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            id: '',
            dokumen: '',
            tgl: '',
            stgl: '',
            keterangan: ''
        }
    })

    // rows single table: each row has { id, nomor_akun, profit_center, keterangan, debet, kredit }
    const [rows, setRows] = useState<any[]>([])
    const [docId, setDocId] = useState<any>(null)
    // dialog control
    const [openDialog, setOpenDialog] = useState(false)
    const [dialogType, setDialogType] = useState<'debet' | 'kredit'>('debet')

    // totals
    const [totalDebet, setTotalDebet] = useState<number>(0)
    const [totalKredit, setTotalKredit] = useState<number>(0)
    useEffect(() => {
        if (!id) return

        const url = apiUrl.apiUrl + `/acc/penyesuaian/detail/${id}`
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
            if (res.data.success) {
                const master = res.data.master
                const details = res.data.detail

                setDocId(master.doc_id)
                // Set master form
                setValue('id', master.id)
                setValue('dokumen', master.dokumen)
                // setValue('stgl', master.tgl)
                setValue(
                    'stgl',
                    master.tgl ? new Date(master.tgl).toISOString().split('T')[0] : ''
                );
                setValue('keterangan', master.keterangan)

                const formatted = details.map((d: any) => ({
                    id: d.id,
                    nomor_akun: d.nomor_akun,
                    nama_akun: d.nama_akun,
                    profit_center: d.accprofitcenter_id,
                    nama_profit_center: d.nama_profit_center,
                    keterangan: d.keterangan,
                    debet: d.is_debet == 1 ? Number(d.total) : 0,
                    kredit: d.is_debet === 0 ? Number(d.total) : 0
                }))

                setRows(formatted)
            }
        })
    }, [id])

    // calculate totals when rows change
    useEffect(() => {
        const sumD = rows.reduce((acc, r) => acc + (Number(r.debet) || 0), 0)
        const sumK = rows.reduce((acc, r) => acc + (Number(r.kredit) || 0), 0)
        setTotalDebet(sumD)
        setTotalKredit(sumK)
    }, [rows])

    const isBalance = totalDebet === totalKredit && totalDebet > 0

    // convertComponent untuk TableGrid: format Debet/Kredit and Action
    const convertComponent = [
        {
            key: 'debet',
            renderCell: (params: any) => (
                <Typography variant='body2'>{Number(params.data.debet || 0).toLocaleString('id-ID')}</Typography>
            )
        },
        {
            key: 'kredit',
            renderCell: (params: any) => (
                <Typography variant='body2'>{Number(params.data.kredit || 0).toLocaleString('id-ID')}</Typography>
            )
        },
        {
            key: 'action',
            renderCell: (params: any) => (
                <Button
                    size='small'
                    color='error'
                    onClick={() => {
                        setRows(prev => prev.filter(r => r.id !== params.data.id))
                    }}
                    disabled={docId != null ? true : false}
                >
                    <Icon icon='mdi:trash-can-outline' />
                </Button>
            )
        }
    ]

    // add new row coming from dialog
    const handleAddRow = (data: any) => {
        // cek duplikasi nomor_akun
        const isDuplicate = rows.some(r => r.nomor_akun === data.nomor_akun)

        if (isDuplicate) {
            Swal.fire({
                icon: 'warning',
                title: 'Nomor Akun Sudah Ada',
                text: `Nomor akun ${data.nomor_akun} sudah ditambahkan sebelumnya`,
                confirmButtonColor: '#3085d6'
            })
            return // hentikan proses tambah row
        }

        const newRow = {
            id: Math.random().toString(36).substr(2, 9),
            nomor_akun: data.nomor_akun || '',
            nama_akun: data.nama_akun || '',
            profit_center: data.profit_center || '',
            nama_profit_center: data.nama_profit_center || '',
            keterangan: data.keterangan || '',
            debet: data.type === 'debet' ? Number(data.total || 0) : 0,
            kredit: data.type === 'kredit' ? Number(data.total || 0) : 0
        }

        setRows(prev => [...prev, newRow])
    }

    const onSave = (masterData: any) => {
        // prepare payload
        const payload = {
            ...masterData,
            details: rows
        }

        Swal.fire({
            title: 'Konfirmasi',
            html: 'Apakah anda ingin menyimpan data ini ?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#337ab7',
            cancelButtonColor: '#ffc107',
            confirmButtonText: 'Simpan'
        }).then(result => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Now loading',
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })

                const url = apiUrl.apiUrl + '/acc/penyesuaian/save'
                axios
                    .post(url, payload, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    .then(response => {
                        if (response.data.success) {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                html: response.data.message,
                                icon: 'success',
                                timer: 1000,
                                timerProgressBar: true,
                                willClose: () => {
                                    const id = response.data.data
                                    navigate(-1)
                                }
                            })
                        } else {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                html: response.data.message,
                                icon: 'error'
                            })
                        }
                    })
                    .catch(error => {
                        Swal.fire({
                            title: 'Pemberitahuan',
                            text: error.message,
                            icon: 'error'
                        })
                    })
            }
        })
    }

    return (
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Grid item xl={12} md={12} xs={12}>
                <Fab variant='extended' color='primary' sx={{ '& svg': { mr: 1 } }} onClick={() => window.history.back()}>
                    <Icon icon='mdi:arrow-left' />
                    Kembali
                </Fab>
                <div className={'content-block dx-card'} style={{ paddingRight: 20, paddingLeft: 20, paddingBottom: 10, paddingTop: 10 }}>
                    <Typography variant='h5' sx={{ textAlign: 'center' }}>
                        Detail Jurnal Memorial
                    </Typography>
                    <Typography variant='body1' sx={{ textAlign: 'center', mb: 2 }}>
                        No. Dokumen : <b>{id}</b>
                    </Typography>
                    <Divider />
                    <form onSubmit={handleSubmit(onSave)}>
                        <CardContent>
                            {/* Master Header */}
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='id'
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label='ID'
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ readOnly: true }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='dokumen'
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label='Document'
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ readOnly: true }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='stgl'
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label='Tanggal Transaksi'
                                                    type='date'
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={12} sx={{ mb: 3 }}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='keterangan'
                                            control={control}
                                            render={({ field }) => <TextField {...field} label='Keterangan' multiline rows={3} />}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 5 }} />

                            {/* Controls for details */}
                            <Stack direction='row' spacing={2} sx={{ mt: 5, mb: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant='contained'
                                    startIcon={<Icon icon='mdi:plus' />}
                                    onClick={() => {
                                        setDialogType('debet')
                                        setOpenDialog(true)
                                    }}
                                    disabled={docId != null ? true : false}
                                >
                                    Tambah Debet
                                </Button>

                                <Button
                                    variant='contained'
                                    color='warning'
                                    startIcon={<Icon icon='mdi:plus' />}
                                    onClick={() => {
                                        setDialogType('kredit')
                                        setOpenDialog(true)
                                    }}
                                    disabled={docId != null ? true : false}
                                >
                                    Tambah Kredit
                                </Button>
                            </Stack>

                            {/* Single Detail Table */}
                            <MsiDatagrid array={rows.map(r => ({
                                id: r.id,
                                // nomor_akun: r.nomor_akun,
                                nama_akun: r.nama_akun,
                                // profit_center: r.profit_center,
                                nama_profit_center: r.nama_profit_center,
                                keterangan: r.keterangan,
                                debet: r.debet,
                                kredit: r.kredit,
                                action: ''
                            }))}
                                hiddenColumns={['id']}
                                customRender={convertComponent}
                            />

                            {/* Totals & Balance */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <Box>
                                    <Typography variant='subtitle1'>
                                        Total Debit: <b>{totalDebet.toLocaleString('id-ID')}</b>
                                    </Typography>
                                    <Typography variant='subtitle1'>
                                        Total Kredit: <b>{totalKredit.toLocaleString('id-ID')}</b>
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                    {isBalance ? (
                                        <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 2, py: 1, borderRadius: 1 }}>
                                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                                                Sudah Balance
                                            </Typography>
                                            <Typography variant='caption'>Debit = Kredit</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ bgcolor: '#fff3e0', color: '#e65100', px: 2, py: 1, borderRadius: 1 }}>
                                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                                                Tidak Balance
                                            </Typography>
                                            <Typography variant='caption'>
                                                Selisih: {Math.abs(totalDebet - totalKredit).toLocaleString('id-ID')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Action buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                                <Button variant='contained' color='primary' type='submit' disabled={!isBalance}>
                                    SIMPAN
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        navigate(-1)
                                    }}
                                >
                                    BATAL
                                </Button>
                            </Box>
                        </CardContent>
                    </form>
                </div>
            </Grid>

            {/* Reuse the same dialog, pass typeDefault */}
            <DialogAddRow
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSubmit={(d: any) => {
                    handleAddRow(d)
                    setOpenDialog(false)
                }}
                typeDefault={dialogType}
            />

            <FloatingJurnalButton
                id={Number(id)}
                tabel="accpenyesuaian"
            />
        </Grid >
    )
}

export default AddMemorialSingleTable
